"use client"

import * as React from "react"
import { buttonVariants, Input } from "@ui/shad"
import { addMonths, addYears, format, isAfter, isBefore, isValid, parse, startOfMonth } from "date-fns"
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react"
import { DayPicker, isDateAfterType, isDateBeforeType, isDateInterval, isDateRange, Matcher } from "react-day-picker"

import { cn } from "../utils"

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  disableInput?: boolean
}

function formatDateInput(value: string): string {
  const numbers = value.replace(/\D/g, "")

  if (numbers.length <= 2) {
    return numbers
  } else if (numbers.length <= 4) {
    return `${numbers.slice(0, 2)}/${numbers.slice(2)}`
  } else {
    return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`
  }
}

function parseDateInput(value: string): Date | null {
  const numbers = value.replace(/\D/g, "")

  if (numbers.length < 8) {
    return null
  }

  const month = numbers.slice(0, 2)
  const day = numbers.slice(2, 4)
  const year = numbers.slice(4, 8)

  const dateString = `${month}/${day}/${year}`
  const parsedDate = parse(dateString, "MM/dd/yyyy", new Date())

  return isValid(parsedDate) ? parsedDate : null
}

function isDateDisabled(
  date: Date,
  disabledMatcher?: Matcher | Matcher[],
  startMonth?: Date,
  endMonth?: Date
): boolean {
  if (startMonth && date < startMonth) return true
  if (endMonth && date > endMonth) return true

  if (!disabledMatcher) return false

  const matchers = Array.isArray(disabledMatcher) ? disabledMatcher : [disabledMatcher]

  return matchers.some((matcher) => {
    if (typeof matcher === "boolean") return matcher
    if (typeof matcher === "function") return matcher(date)
    if (matcher instanceof Date) return date.toDateString() === matcher.toDateString()
    if (Array.isArray(matcher)) return matcher.some((d) => date.toDateString() === d.toDateString())
    if (isDateRange(matcher)) {
      const { from, to } = matcher
      if (from && to) return date >= from && date <= to
      if (from) return date >= from
      if (to) return date <= to
      return false
    }
    if (isDateInterval(matcher)) return date > matcher.before && date < matcher.after
    if (isDateBeforeType(matcher)) return date < matcher.before
    if (isDateAfterType(matcher)) return date > matcher.after
    if ("dayOfWeek" in matcher) {
      return Array.isArray(matcher.dayOfWeek)
        ? matcher.dayOfWeek.includes(date.getDay())
        : matcher.dayOfWeek === date.getDay()
    }
    return false
  })
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  fixedWeeks = true,
  month,
  onMonthChange,
  defaultMonth,
  disableInput = false,
  ...props
}: CalendarProps) {
  const selected = "selected" in props ? props.selected : undefined
  const [internalMonth, setInternalMonth] = React.useState(
    month || defaultMonth || (selected instanceof Date ? selected : new Date())
  )
  const [dateInput, setDateInput] = React.useState("")
  const [previewDate, setPreviewDate] = React.useState<Date | null>(null)
  const [isInputFocused, setIsInputFocused] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const skipNextFocusRef = React.useRef(false)

  const currentMonth = month ?? internalMonth
  const startMonth = props.startMonth ?? props.fromDate
  const endMonth = props.endMonth ?? props.toDate

  const canNavigateTo = (newMonth: Date) => {
    const normalizedMonth = startOfMonth(newMonth)

    if (startMonth && isBefore(normalizedMonth, startOfMonth(startMonth))) return false
    if (endMonth && isAfter(normalizedMonth, startOfMonth(endMonth))) return false

    return true
  }

  const handleMonthChange = (newMonth: Date) => {
    if (!canNavigateTo(newMonth)) return

    setInternalMonth(newMonth)
    onMonthChange?.(newMonth)
  }

  const selectDate = (date: Date, sourceEvent?: React.KeyboardEvent<HTMLInputElement>) => {
    if ("onSelect" in props && typeof props.onSelect === "function") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(props.onSelect as any)(date, date, {} as Record<string, boolean>, sourceEvent as unknown as MouseEvent)
    }
  }

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!disableInput && !isInputFocused && /^[0-9]$/.test(e.key)) {
        e.preventDefault()
        skipNextFocusRef.current = true
        setDateInput(e.key)
        inputRef.current?.focus()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [disableInput, isInputFocused])

  const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatDateInput(e.target.value)
    setDateInput(formatted)

    const parsedDate = parseDateInput(formatted)
    setPreviewDate(parsedDate)
    if (parsedDate) {
      handleMonthChange(parsedDate)
    }
  }

  const handleInputFocus = () => {
    setIsInputFocused(true)
    if (skipNextFocusRef.current) {
      skipNextFocusRef.current = false
      return
    }

    setDateInput(format(currentMonth, "MM/dd/yyyy"))
    setTimeout(() => inputRef.current?.select(), 0)
  }

  const handleInputBlur = () => {
    setIsInputFocused(false)
    setDateInput("")
    setPreviewDate(null)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && previewDate) {
      e.preventDefault()
      if (
        !isDateDisabled(previewDate, props.disabled, props.startMonth ?? props.fromDate, props.endMonth ?? props.toDate)
      ) {
        selectDate(previewDate, e)
      }
      inputRef.current?.blur()
    }
  }

  const navButtonClass = cn(
    buttonVariants({ variant: "outline" }),
    "size-6 bg-transparent p-0 disabled:pointer-events-none disabled:opacity-50"
  )

  const previousYear = addYears(currentMonth, -1)
  const previousMonth = addMonths(currentMonth, -1)
  const nextMonth = addMonths(currentMonth, 1)
  const nextYear = addYears(currentMonth, 1)

  return (
    <div
      className={cn("p-3", className)}
      onClickCapture={(e) => {
        const outsideDay = (e.target as HTMLElement).closest(".outside")
        if (!outsideDay) return
        e.stopPropagation()
        const dayNum = parseInt(outsideDay.textContent || "", 10)
        const adjacentMonth = addMonths(currentMonth, dayNum > 20 ? -1 : 1)
        handleMonthChange(adjacentMonth)
        const clickedDate = new Date(adjacentMonth.getFullYear(), adjacentMonth.getMonth(), dayNum)
        if (
          !isDateDisabled(
            clickedDate,
            props.disabled,
            props.startMonth ?? props.fromDate,
            props.endMonth ?? props.toDate
          )
        ) {
          selectDate(clickedDate)
        }
      }}
    >
      <div className="flex items-center justify-between gap-3 pb-2">
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            className={navButtonClass}
            onClick={() => handleMonthChange(previousYear)}
            disabled={!canNavigateTo(previousYear)}
            aria-label="Go to previous year"
          >
            <ChevronsLeft className="size-3.5" />
          </button>
          <button
            type="button"
            className={navButtonClass}
            onClick={() => handleMonthChange(previousMonth)}
            disabled={!canNavigateTo(previousMonth)}
            aria-label="Go to previous month"
          >
            <ChevronLeft className="size-3.5" />
          </button>
        </div>
        <Input
          ref={inputRef}
          type="text"
          readOnly={disableInput}
          value={isInputFocused && !disableInput ? dateInput : format(currentMonth, "MMM yyyy")}
          onChange={handleDateInputChange}
          onFocus={disableInput ? undefined : handleInputFocus}
          onBlur={disableInput ? undefined : handleInputBlur}
          onKeyDown={disableInput ? undefined : handleKeyDown}
          placeholder="MM/DD/YYYY"
          className={cn("h-7 w-28 text-center text-sm font-medium", disableInput && "cursor-default focus:ring-0")}
          maxLength={10}
        />
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            className={navButtonClass}
            onClick={() => handleMonthChange(nextMonth)}
            disabled={!canNavigateTo(nextMonth)}
            aria-label="Go to next month"
          >
            <ChevronRight className="size-3.5" />
          </button>
          <button
            type="button"
            className={navButtonClass}
            onClick={() => handleMonthChange(nextYear)}
            disabled={!canNavigateTo(nextYear)}
            aria-label="Go to next year"
          >
            <ChevronsRight className="size-3.5" />
          </button>
        </div>
      </div>
      <DayPicker
        showOutsideDays={showOutsideDays}
        fixedWeeks={fixedWeeks}
        month={currentMonth}
        onMonthChange={handleMonthChange}
        modifiers={previewDate ? { preview: previewDate } : undefined}
        classNames={{
          months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
          month: "space-y-4",
          caption: "hidden",
          month_caption: "hidden",
          nav: "hidden",
          month_grid: "w-full border-collapse space-y-1",
          weekdays: "flex",
          weekday: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
          week: "flex w-full mt-2",
          day: cn(
            "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&.outside:has([aria-selected])]:bg-accent/50",
            props.mode === "range"
              ? "[&.range-end]:rounded-r-md [&.range-start]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
              : "[&:has([aria-selected])]:rounded-md"
          ),
          day_button: cn(
            buttonVariants({ variant: "ghost" }),
            "day-button h-8 w-9 p-0 font-normal aria-selected:opacity-100"
          ),
          range_start: "range-start",
          range_end: "range-end",
          selected:
            "[&>.day-button]:bg-primary [&>.day-button]:text-primary-foreground [&>.day-button:hover]:bg-primary [&>.day-button:hover]:text-primary-foreground [&>.day-button:focus]:bg-primary [&>.day-button:focus]:text-primary-foreground",
          today: "[&>.day-button]:bg-accent [&>.day-button]:text-accent-foreground",
          outside:
            "outside text-muted-foreground [&>.day-button[aria-selected]]:bg-accent/50 [&>.day-button[aria-selected]]:text-muted-foreground",
          disabled: "text-muted-foreground opacity-50",
          range_middle:
            "[&>.day-button[aria-selected]]:bg-accent [&>.day-button[aria-selected]]:text-accent-foreground",
          hidden: "invisible",
          ...classNames,
        }}
        modifiersClassNames={{
          preview: "ring-2 ring-blue-accent ring-inset font-semibold",
        }}
        {...props}
      />
    </div>
  )
}
Calendar.displayName = "Calendar"

export { Calendar }
